import { Hint } from "igfd-ui";
import {
  type CustomContentProps,
  SnackbarContent,
  SnackbarProvider,
  useSnackbar as useSnackbarNotistack,
} from "notistack";

import React, { forwardRef } from "react";

import { ErrorInformer } from "@dashboard/components/errors/ErrorInformer";

export type Snackbar = {
  success: (message: string) => void;
  error: (message: string) => void;
};

/**
 * A stripped down version of the useSnackbar from notistack with only success and error
 * @returns
 */
export const useSnackbar = (): Snackbar => {
  const { enqueueSnackbar } = useSnackbarNotistack();

  const success = (message: string) => {
    enqueueSnackbar(message, {
      variant: "success",
    });
  };

  const error = (message: string) => {
    enqueueSnackbar(message, {
      variant: "error",
    });
  };

  return { success, error };
};

export const DashboardSnackbarProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  type HintProps = {
    message: string;
  } & Omit<CustomContentProps, "message">;

  // from https://github.com/iamhosseindhv/notistack/issues/440#issuecomment-955632581
  // https://notistack.com/features/customization#custom-component
  const SuccessHint = forwardRef<HTMLDivElement, HintProps>(
    ({ message }, ref) => (
      <SnackbarContent ref={ref}>
        <Hint hintValue={message} severity={"success"} />
      </SnackbarContent>
    )
  );

  const ErrorHint = forwardRef<HTMLDivElement, HintProps>(
    ({ message }, ref) => (
      <SnackbarContent ref={ref}>
        <Hint hintValue={message} severity={"error"} />
      </SnackbarContent>
    )
  );

  return (
    <SnackbarProvider
      maxSnack={3}
      autoHideDuration={3000}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      Components={{
        default: SuccessHint,
        success: SuccessHint,
        error: ErrorHint,
      }}
    >
      {children}
      {/* This component shows errors from the Redux store */}
      <ErrorInformer />
    </SnackbarProvider>
  );
};
